import { useState, createContext, useContext, useCallback } from "react";
import i18next, { exists } from "i18next";
import { ITDContext, ConsoleLog, ModalManager, ModalProps } from "@inntechcorp/it-design";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';

import { INNError, usePostFetch } from "Api/hooks/usePostFetch";
import { LoginDataPropsType } from "./types/LoginDataPropsType";
import { DefaultLogin } from "./types/DefaultLogin";
import { GetLocalState, SetLocalState } from "Connectivity/LocalStorage";
import { BOContextPropsType } from "./types/BOContextPropsType";
import ModalData from "Helpers/modal/ModalData";
import { LoginResultPropsType } from "./types/LoginResultPropsType";
import { DateFormatPropsType } from "./types/DateFormatPropsType";

//import tr from 'antd/locale/tr_TR';
//import en from 'antd/locale/en_US';

import trLocale from 'antd/locale/tr_TR';
import enLocale from 'antd/locale/en_US';

import 'dayjs/locale/tr';
import 'dayjs/locale/en';

import { ChildrenProps } from "Types/ChildrenProps";
import { AffiliateAccountTypes } from "Types/enums";
import { ConfigProvider } from "antd";
import { toast } from "sonner";

dayjs.extend(relativeTime);

const BackOfficeContext = createContext<BOContextPropsType>({} as BOContextPropsType);
      BackOfficeContext.displayName = "BackOfficeContext";
      
export const BackOfficeProvider = ({ children }:{children:ChildrenProps}) => {

  const { 
    changeLanguage,
    changeLocale, 
    changeDateFormat, 
    changeTheme 
  } = useContext(ITDContext);

  const { showModal, removeAllModals }          = ModalManager.useModal();
  const { fetch }                               = usePostFetch();

  const [login,             SetLogin]           = useState<LoginDataPropsType>(DefaultLogin);
  const [isLoggedIn,        SetLoggedIn]        = useState(false);
  const [isLoggingOut,      SetLoggingOut]      = useState(false);
  const [isOnline,          SetIsOnline]        = useState(false);
  const [showBodyScroll,    SetShowBodyScroll]  = useState(true);
  const [theme,             SetTheme]           = useState("");
  const [flag,              SetFlag]            = useState("");
  const [language,          SetLanguage]        = useState("");
  const [locale,            SetLocale]          = useState("");
  const [currency,          SetCurrency]        = useState("");
  const [timezone,          SetTimeZone]        = useState("");
  const [dateFormat,        SetDateFormat]      = useState<DateFormatPropsType>({
    "TinyDate"      : "",
    "MonthYear"     : "",
    "Moment"        : "",
    "Date"          : "",
    "Time"          : "",
    "FullDate"      : "",
    "HumanDate"     : "",
    "DateTime"      : "",
    "FullDateTime"  : ""
  });

  const { t } = i18next;

  const addNotificationSuccess = useCallback((message = "", description = "") => {
    toast.success(
      exists(message) ? t(message) : message,
      {
        description : exists(description) ? t(description) : description,
      }
    );
  }, [t]);
  
  const addNotificationError = useCallback((message = "", description = "") => {
    toast.error(
      exists(message) ? t(message) : message,
      {
        description : exists(description) ? t(description) : description,
      }
    );
  }, [t]);

  /* ---------------------------------------- */
  
  const getLanguageDataBySlug = useCallback((slug:any) => { 
    return GetLocalState("languages").filter((l:any) => l.Slug === slug)[0];
  }, []);

  /* ---------------------------------------- */

  const ChangeLogin = async (
    data            : LoginResultPropsType | LoginDataPropsType | null = null,
    parseLogin      : boolean = true,
    defaultValues   : any = null
  ) => {

    if ( !data ) {
      console.error("Login data can't provided!");
      return;
    }

    if ( parseLogin && (data as LoginResultPropsType).regular.profile_photo === "" ) {
      (data as LoginResultPropsType).regular.profile_photo = GetLocalState("assets").icons.DefaultProfilePhoto;
    }
    
    const loginData = parseLogin ? getLoginDataByData(data as LoginResultPropsType) : data as LoginDataPropsType;
    
    SetLoggedIn(true);
    SetLogin(loginData);
    SetLocalState("login", loginData);

    /**
     * Set the currency to the currency of the login data.
     */
    ChangeCurrency(loginData.clientCurrencyCode || defaultValues.Currency);
    
  };

  const ChangeLogout = useCallback((status = "", message?:string) => {

    ConsoleLog.single("🚨 ChangeLogout > Status: " + status);

    SetLoggingOut(true);
    removeAllModals();

    if ( status === "tokenIsExpired" || status === "userNotLoggedIn" ) {
      if ( message && Cookies.get("aff-token") !== undefined ) {
        addNotificationError(message);
      }
      RemoveLoginTokens();
      return;
    }

    else if ( 
      status === "badGateway" 
      || status === "error" 
      || status === "serviceTemporarilyUnavailable" 
      || status === "internalServerError" 
      || status === "noResponseFromApiPath" 
      || status === "unhandledError" 
    ) {
      RemoveLoginTokens();
      addNotificationError(`forms.notifications.${status}`);
      return;
    }

    fetch(
      "post",
      `/api/private/logout`,
      {},
      { 
        onFetched: () => {
          RemoveLoginTokens();
          addNotificationSuccess("forms.notifications.logoutSuccess")
        },
        onError: (err?: INNError):void => {
          console.error("err", err?.detail);
          RemoveLoginTokens();
          //addNotificationError("forms.notifications.logoutFailed")
        }
      }
    );

  }, [removeAllModals, addNotificationError, addNotificationSuccess, fetch]);

    /**
   * 
   * Ön tanımlı modal açmak için kullanılır. Config'ten gelen
   * modal değerlerine göre açar ve içeriğini belirler. Ön tanımlı
   * olmayan modal açmak için ITD kütüphanesindeki ModalManager 
   * kullanılması gereklidir.
   * 
   * @param {*} name Açılacak modal'ın adı
   * 
   */
  const ShowModal = (id:string, name:string, props?:Partial<ModalProps>) => {

    let modalProps:ModalProps = {
      id                    : id,
      title                 : "",
      width                 : "440px",
      closeOnClickOutside   : true,
      preventESC            : false,
      showOkButton          : true,
      showCancelButton      : true,
      showCloseButton       : true,
      okButtonText          : t("forms.buttons.OK"),
      cancelButtonText      : t("forms.buttons.cancel"),
      customProps           : props?.customProps ? props.customProps : {},
      onOk                  : null,
      onCancel              : null,
      content               : null,
      context               : null,
      okButtonProps         : null,
      cancelButtonProps     : null,
      removeOthers          : false,
      ...props
    }

    showModal(ModalData(name, modalProps, getContextValue));

  };

  const RemoveLoginTokens = useCallback(async () => {
    SetLocalState("login", {});
    Cookies.remove("aff-token");
    SetLogin(DefaultLogin);
    SetLoggedIn(false);
    SetLoggingOut(false);
  }, []);

  const UpdateLogin = useCallback(<K extends keyof LoginDataPropsType>(key: K, value: LoginDataPropsType[K]) => {
    SetLogin(prevLogin => {
      const data = {...prevLogin};
      data[key] = value;
      SetLocalState("login", data);
      return data;
    });
  }, []);

  const ChangeTheme = useCallback((value:string) => {
    Cookies.set("aff-theme", value);
    SetTheme(value);
    changeTheme(value);
  }, [changeTheme]);
  
  const ChangeFlag = useCallback((value:string) => {
    Cookies.set("aff-flag", value);
    SetFlag(value);
  }, []);

  const ChangeLocale = useCallback((value:string) => {
    Cookies.set("aff-locale", value);
    changeLocale(value);
    SetLocale(value);
  }, []);
  
  const ChangeLanguage = useCallback((value:string) => {

    SetLanguage(value);
    ChangeLocale(value);
    i18next.changeLanguage(value);
    changeLanguage(value);
    Cookies.set("aff-language", value);
    dayjs.locale(value);

    const format = getLanguageDataBySlug(value).Format;
    SetDateFormat(format);
    changeDateFormat(format.Moment);

  }, [changeLocale, changeLanguage, changeDateFormat, getLanguageDataBySlug]);

  const ChangeCurrency = useCallback((value:string) => {
    Cookies.set("aff-currency", value);
    SetCurrency(value);
  }, []);

  const ChangeTimezone = useCallback((value:string) => {
    Cookies.set("aff-timezone", value);
    SetTimeZone(value);
  }, []);

  const ChangeDateFormat = useCallback((format:DateFormatPropsType) => {
    SetDateFormat(format);
  }, []);

  const ChangeIsOnline = (value:boolean) => {
    
    if ( value ) {
      ConsoleLog.single("📡 Connection restored. Internet connection active.",);
    }
    else {
      ConsoleLog.single("🕯️ Connection error. There is no Internet connection.", "error");
    }

    SetIsOnline(value);

    if ( !value ) ShowModal("disconnect-modal", "disconnect");
    
  };

  const ChangeOTPStatus = useCallback((value:boolean) => {

    SetLogin(prevLogin => {
      const updatedLogin      = Object.assign({}, prevLogin);
      updatedLogin.OTPEnabled = value;
      SetLocalState("login", updatedLogin);
      return updatedLogin;
    });
    
  }, []);

  
  /* ---------------------------------------- */

  /*
  function wait(ms) {
    return new Promise( (resolve) => {setTimeout(resolve, ms)});
  }
  */

  const getLocale = useCallback(() => {
    if ( language === "tr" ) return trLocale;
    return enLocale;
  }, [language]);

  
  const getLoginDataByData = useCallback((data:LoginResultPropsType): LoginDataPropsType => {
    
    let _data: LoginDataPropsType = {
      type                  : data.regular.affiliate.type as AffiliateAccountTypes,
      username              : data.regular.affiliate.username,
      fullName              : data.regular.affiliate.full_name,
      email                 : data.regular.affiliate.email,
      firstName             : data.regular.affiliate.first_name,
      middleName            : data.regular.affiliate.middle_name,
      lastName              : data.regular.affiliate.last_name,
      language              : data.regular.affiliate.language_code,
      forceChangePassword   : data.regular.force_change_password,
      lastLoginCity         : data.regular.location.city,
      lastLoginCountry      : data.regular.location.country,
      OTP                   : data.otp,
      token                 : data.regular.token,
      profilePhoto          : data.regular.profile_photo,
      ip                    : data.regular.ip,
      clientCurrencyCode    : data.regular.client_currency_code,
      clientLanguageCodes   : data.regular.client_language_codes,
      clientOTPEnabled      : data.regular.client_otp_enabled,
      OTPEnabled            : data.regular.affiliate.otp_enabled || false,
      device                : {
        browser   : data.regular.device.browser,
        device    : data.regular.device.device,
        isMobile  : data.regular.device.is_mobile
      },
      location              : {
        city    : data.regular.location.city,
        country : data.regular.location.country
      },
      client                : {
        id          : data.regular.affiliate.client.id ? data.regular.affiliate.client.id : "[No client id]",
        name        : data.regular.affiliate.client.name ? data.regular.affiliate.client.name : "[No client name]",
        slug        : data.regular.affiliate.client.slug ? data.regular.affiliate.client.slug : "[No client slug]",
        siteName    : data.regular.affiliate.client.site_name ? data.regular.affiliate.client.site_name : "[No client site name]",
        domain      : data.regular.affiliate.client.domain ? data.regular.affiliate.client.domain : "[No client domain]"
      }
    }

    return _data;

  }, []);

  /* ---------------------------------------- */
  
  const getContextValue: BOContextPropsType = {
    ShowModal,
    ChangeLogout,
    ChangeOTPStatus,
    RemoveLoginTokens,
    UpdateLogin,
    login,              ChangeLogin,
    theme,              ChangeTheme,
    flag,               ChangeFlag,
    language,           ChangeLanguage,
    locale,             ChangeLocale,
    currency,           ChangeCurrency,
    timezone,           ChangeTimezone,
    dateFormat,         ChangeDateFormat,
    isOnline,           ChangeIsOnline,
    showBodyScroll,     SetShowBodyScroll,
    isLoggedIn,
    isLoggingOut
  }

  return (
    <BackOfficeContext.Provider value={getContextValue}>
      <ConfigProvider locale={getLocale()}>
        {children}
      </ConfigProvider>
    </BackOfficeContext.Provider>
  );
};

export default BackOfficeContext;

